import React, {useContext, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'

import {useBrand} from '@daedalus/core/src/_web/brand/hooks/useBrand'
import {useRouter} from '@daedalus/core/src/_web/router/context/RouterContext'
import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Team
} from '@daedalus/core/src/analytics/types/Events'
import {getWhiteLabelUserId} from '@daedalus/core/src/auth/modules/selectors'
import {getUrlParam} from '@daedalus/core/src/utils/url'

import {SusiContext} from '../../authentication/SUSI/bottomSheet/SusiBottomSheet/SusiProvider'
import {clearPendingEnable} from '../slice'
import {selectPendingEnable} from '../slice/selectors'
import {PriceWatchCTAParams} from '../types'
import {getCallbackUrl} from '../utils'
import {usePriceWatch} from './usePriceWatch'
import {usePriceWatchToggleToast} from './usePriceWatchToggleToast'

export const usePriceWatchToggleWebLogic = (props: PriceWatchCTAParams) => {
  const {setCallbackUrl} = useContext(SusiContext)
  const {replace, push} = useRouter()
  const {
    isAuthenticated,
    hotel,
    checkIn,
    checkOut,
    shouldRedirectToAp,
    hotelContext,
    getSearchContext,
    setWebBottomSheetOpen,
    alertsManagementUrl,
    componentName
  } = props
  const searchContext = useSelector(getSearchContext)
  const {brandIsHolidayPirates} = useBrand()
  const isAuthenticatedWithWhiteLabelUser = useSelector(getWhiteLabelUserId)

  const dispatch = useDispatch()
  const queryParams = new URLSearchParams(location.search)

  const pendingEnable = useSelector(selectPendingEnable)

  const onPriceWatchInitialized = () => {
    setShouldShowPriceWatchEnabledMessage(true)

    const component = queryParams.get('componentPriceWatch')

    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.PriceAlertEnable,
        action: Action.Succeeded,
        team: Team.Retention,
        analyticsContext: {
          [AnalyticsContext.SearchContext]: searchContext,
          [AnalyticsContext.HotelContext]: hotelContext
        },
        payload: {
          isUserSignedIn: props.isAuthenticated
        },
        component: component || componentName
      })
    )

    if (component) {
      queryParams.delete('componentPriceWatch')
      replace({search: queryParams.toString()})
    }

    // In case the toggle in on SRP, we cleanup the param after initialize the price watch for that hotel
    if (!shouldRedirectToAp) {
      if (queryParams.get('enablePriceWatch') === hotel.objectID) {
        queryParams.delete('enablePriceWatch')

        replace({search: queryParams.toString()})
      }
    }
  }

  const onToastClick = (event: React.SyntheticEvent) => {
    event?.preventDefault()
    event?.stopPropagation()
    event?.nativeEvent.stopImmediatePropagation()

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.PriceAlertToast,
        action: Action.Clicked,
        team: Team.Retention,
        analyticsContext: {
          [AnalyticsContext.SearchContext]: searchContext,
          [AnalyticsContext.HotelContext]: hotelContext
        }
      })
    )

    alertsManagementUrl && push(alertsManagementUrl)
  }

  const params = {
    ...props,
    onPriceWatchInitialized
  }

  const {priceWatchEntry, isPriceWatchOn, cancelPriceWatch, initPriceWatch} =
    usePriceWatch(params)

  const {
    setShouldShowPriceWatchEnabledMessage,
    setShouldShowPriceWatchDisabledMessage
  } = usePriceWatchToggleToast({
    isPriceWatchOn,
    hotel,
    checkIn,
    checkOut,
    isEnabledToTriggerToggle: Boolean(isAuthenticated),
    shouldShowNavigationIcon: Boolean(alertsManagementUrl),
    onToastClick
  })

  const handleClick = (event: React.SyntheticEvent<Element, Event>) => {
    event?.preventDefault()
    event?.stopPropagation()

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.PriceAlertToggle,
        action: Action.Clicked,
        team: Team.Retention,
        analyticsContext: {
          [AnalyticsContext.SearchContext]: searchContext,
          [AnalyticsContext.HotelContext]: hotelContext
        },
        payload: {
          isUserSignedIn: isAuthenticated,
          from: isPriceWatchOn ? 'enabled' : 'disabled',
          to: !isPriceWatchOn ? 'enabled' : 'disabled'
        },
        component: componentName
      })
    )

    if (!isAuthenticated) {
      // Trigger the login button click
      if (brandIsHolidayPirates && !isAuthenticatedWithWhiteLabelUser) {
        const loginButton = document.getElementById('hp-login-button')
        loginButton?.click()
        return
      }

      setCallbackUrl(
        getCallbackUrl({
          ...props,
          hotelId: hotel.objectID,
          shouldRedirectToAp
        })
      )
      setWebBottomSheetOpen()

      return
    }

    if (isAuthenticated && !isPriceWatchOn) {
      initPriceWatch()
      return
    }

    if (isPriceWatchOn) {
      cancelPriceWatch()
      setShouldShowPriceWatchDisabledMessage(true)
      return
    }
  }

  const hotelId = hotel?.objectID

  const enablePriceWatchAfterAuth = () => {
    if (!isAuthenticated) return

    // Handle enable via URL param (?enablePriceWatch=hotelId)
    const enablePriceWatchParam = getUrlParam({
      search: location.search,
      param: 'enablePriceWatch'
    })
    const componentParam = queryParams.get('componentPriceWatch')
    const isUrlMatch = hotelId === enablePriceWatchParam
    const isComponentMatch = componentName === componentParam

    if (isUrlMatch && isComponentMatch && !isPriceWatchOn) {
      initPriceWatch()
      return
    }

    // Handle enable via Redux (pendingEnable) -> Derived from OTP flow
    if (
      pendingEnable?.hotelId !== hotelId ||
      pendingEnable?.component !== componentName
    )
      return

    if (!isPriceWatchOn) {
      initPriceWatch(pendingEnable?.component)
    }

    if (isPriceWatchOn && pendingEnable?.hotelId === hotelId) {
      setShouldShowPriceWatchEnabledMessage(true)
    }

    dispatch(clearPendingEnable())
  }

  const disablePriceWatchViaURL = () => {
    const unsubscribePriceWatchParam = queryParams.get('unsubscribePriceWatch')
    const hotelIdURL = queryParams.get('hotelId')

    const shouldUnsubscribePriceWatch =
      unsubscribePriceWatchParam && hotelId === hotelIdURL

    if (shouldUnsubscribePriceWatch) {
      cancelPriceWatch()
      setShouldShowPriceWatchDisabledMessage(true)
      queryParams.delete('unsubscribePriceWatch')
      replace({search: queryParams.toString()})
    }
  }

  useEffect(() => {
    // Re-evaluate when either the URL changes OR the user authentication status changes.
    // The latter is necessary for flows (e.g. One-Time-Password) that do not perform a full
    // redirect but still complete authentication in-place.
    enablePriceWatchAfterAuth()
    disablePriceWatchViaURL()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, isAuthenticated, pendingEnable, isPriceWatchOn])

  useEffect(() => {
    // Cleanup enablePriceWatch when unmount component
    return () => {
      if (queryParams.get('enablePriceWatch')) {
        queryParams.delete('enablePriceWatch')
        replace({search: queryParams.toString()})
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isToggleOn = isPriceWatchOn && Boolean(isAuthenticated)

  return {
    handleClick,
    isToggleOn,
    priceWatchEntry,
    cancelPriceWatch,
    initPriceWatch
  }
}
